import { trpc } from './trpc';
import { inferRouterInputs, inferRouterOutputs } from '@trpc/server';
import { AppRouter } from '@tagadapay/trpc-types';
import { useToast } from '@/components/ui/use-toast';

type OutputRouter = inferRouterOutputs<AppRouter>;
type InputRouter = inferRouterInputs<AppRouter>;

// Output types
export type CustomerListOutput = OutputRouter['customers']['list'];
export type Customer = CustomerListOutput['customers'][number];
export type CustomerDetail = OutputRouter['customers']['getById'];
export type CustomerSearchOutput = OutputRouter['customers']['search'];
export type CustomerInfoCmsOutput = OutputRouter['customers']['getInfoCms'];
export type IsReturningCustomerOutput = OutputRouter['customers']['isReturning'];
export type CustomerCreditsOutput = OutputRouter['customers']['getCredits'];

// Input types
export type CustomerSearchInput = InputRouter['customers']['search'];
export type CustomerListInput = InputRouter['customers']['list'];
export type CustomerGetByIdInput = InputRouter['customers']['getById'];
export type CustomerUpdateInput = InputRouter['customers']['update'];
export type CustomerCreateInput = InputRouter['customers']['create'];
export type CustomerCreateDeviceInput = InputRouter['customers']['createDevice'];
export type CustomerInfoCmsInput = InputRouter['customers']['getInfoCms'];
export type IsReturningCustomerInput = InputRouter['customers']['isReturning'];
export type CustomerUpdateCRMInput = InputRouter['customers']['updateCRM'];

/**
 * Hook to search customers
 */
export function useCustomersSearch(params?: CustomerSearchInput) {
    return trpc.customers.search.useQuery(params || {
        searchValue: null,
        customerId: null,
        storeId: null,
    });
}

/**
 * Hook to list customers with pagination and filtering
 */
export function useCustomersList(params?: CustomerListInput) {
    return trpc.customers.list.useQuery(params || {
        search: undefined,
        pagination: {
            page: 1,
            pageSize: 10,
        },
        sortBy: {
            field: 'createdAt',
            direction: 'desc',
        },
        filters: {},
    });
}

/**
 * Hook to get customer by ID
 */
export function useCustomerById(customerId: string) {
    return trpc.customers.getById.useQuery({ customerId });
}

/**
 * Hook to update customer
 */
export function useUpdateCustomer() {
    const { toast } = useToast();
    const utils = trpc.useUtils();

    return trpc.customers.update.useMutation({
        onSuccess: () => {
            utils.customers.list.invalidate();
            utils.customers.getById.invalidate();
            utils.customers.search.invalidate();
            toast({
                title: 'Success',
                description: 'Customer updated successfully',
            });
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: error.message,
                variant: 'destructive',
            });
        },
    });
}

/**
 * Hook to create customer device
 */
export function useCreateCustomerDevice() {
    const { toast } = useToast();
    const utils = trpc.useUtils();

    return trpc.customers.createDevice.useMutation({
        onSuccess: () => {
            utils.customers.getById.invalidate();
            toast({
                title: 'Success',
                description: 'Customer device created successfully',
            });
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: error.message,
                variant: 'destructive',
            });
        },
    });
}

/**
 * Hook to create a customer
 */
export function useCreateCustomer() {
    const { toast } = useToast();
    const utils = trpc.useUtils();

    return trpc.customers.create.useMutation({
        onSuccess: () => {
            utils.customers.list.invalidate();
            utils.customers.search.invalidate();
            toast({
                title: 'Success',
                description: 'Customer created successfully',
            });
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: error.message,
                variant: 'destructive',
            });
        },
    });
}

/**
 * Hook to get customer credits
 */
export function useCustomerCredits(customerId: string) {
    return trpc.customers.getCredits.useQuery(
        { customerId },
        { enabled: !!customerId },
    );
}

/**
 * Hook to adjust customer credits
 */
export function useAdjustCustomerCredits() {
    const { toast } = useToast();
    const utils = trpc.useUtils();

    return trpc.customers.adjustCredits.useMutation({
        onSuccess: (_data, variables) => {
            utils.customers.getCredits.invalidate({ customerId: variables.customerId });
            toast({
                title: 'Success',
                description: 'Credits adjusted successfully',
            });
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: error.message,
                variant: 'destructive',
            });
        },
    });
}

/**
 * Hook to get customer CMS info
 */
export function useCustomerInfoCms(params: CustomerInfoCmsInput) {
    return trpc.customers.getInfoCms.useQuery(params);
}

/**
 * Hook to check if customer is returning
 */
export function useIsReturningCustomer(params: IsReturningCustomerInput) {
    return trpc.customers.isReturning.useQuery(params);
}

/**
 * Hook to export customers as CSV (date range + field multi-select).
 * The mutation returns `{ filename, mimeType, content }` — the caller is
 * responsible for triggering the browser download.
 */
export function useExportCustomersCsv() {
    const { toast } = useToast();
    return trpc.customers.exportCsv.useMutation({
        onError: (error) => {
            toast({
                title: 'Error',
                description: error.message,
                variant: 'destructive',
            });
        },
    });
}

/**
 * Hook to update customer info from CRM (addresses, name, phone)
 */
export function useUpdateCustomerCRM() {
    const { toast } = useToast();
    const utils = trpc.useUtils();

    return trpc.customers.updateCRM.useMutation({
        onSuccess: (_data, variables) => {
            utils.customers.getById.invalidate({ customerId: variables.customerId });
            utils.customers.list.invalidate();
            utils.customers.search.invalidate();
            toast({
                title: 'Success',
                description: 'Customer updated successfully',
            });
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: error.message,
                variant: 'destructive',
            });
        },
    });
}
